import { Component, createRef, Fragment } from 'react';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { Logger } from '../../../models/logger';
import { EnvironmentService } from '../../../services/environment.service';
import { MotivesService } from '../../../services/motives.service';
import { WindowService } from '../../../services/window.service';
import { isNaN, isNumber, parseInt, toString } from '../../../utils/lodash-min';
import { reloadAfterExecute } from '../../../utils/react.utils';
import { MotiveConfig, MotiveLocationType } from '../../../utils/request.utils';
import { StringUtils } from '../../../utils/string-utils';
import { untilDestroyed } from '../../../utils/until-destroyed';
import styles from './motive.component.module.scss';
import { requestAdFill } from './ad-slot.util';
import { AD_CLIENT } from './ad-slots';
import { adSpaceFor, adsWouldShowFor } from './ads-would-show';
import { creativeIsVisible } from './creative-visible';
// import FreestarAdSlot from '@freestar/pubfig-adslot-react-component'
import { WindowSize, WindowUtils } from '../../../utils/window.utils';
import { BrowserUtils } from '../../../ui-utils/browser-utils';


const defaultOptions = {
    showAds: true,
    className: 'width_major',
}

interface MotiveComponentState {
    showAds?: boolean;
    /**
     * The user closed this ad themselves, as opposed to the slot never being enabled. Only the
     * first case earns an upgrade offer -- it is the one moment we know they do not want ads.
     */
    dismissedByUser?: boolean;
    /**
     * R14-Q8: AdSense stamped this unit's `<ins>` `data-ad-status="filled"` -- a creative is on
     * screen. The close key exists only then: an unstamped (ad blocker) or `unfilled` unit is blank
     * space, and a floating "×" over blank space is what QA saw three of on /downloads.
     */
    filled?: boolean;
    /**
     * R14-SH6 (QA R14_QA_studio_2 group 13): `filled` is AdSense's verdict; this is the measurement -- the
     * `<ins>`, its container and the creative iframe all have a rendered box (`creativeIsVisible`),
     * re-read on every mutation of the unit, on its resize and on the window's. The key needs both.
     */
    creativeVisible?: boolean;
    styles: { [key: string]: string }
}

/** AdSense's own verdict on the `<ins>`: `filled` or `unfilled`. */
const AD_STATUS = 'data-ad-status';

interface MotiveComponentProps {
    children?: any;
    showAds?: boolean;
    data?;
    containerWidth?: string;
    containerHeight?: string;
    adWidth?: string;
    adHeight?: string;
    className?: 'width_major' | 'height_major' | 'width_major_md' | 'rect_med';
    location?: MotiveLocationType;
}

export class MotiveComponent extends Component<MotiveComponentProps, MotiveComponentState> {
    logger = Logger.getLogger('MotiveComponent');

    mergedOptions: MotiveComponentProps = {} as any;
    currentMotiveSlotIsEnabled = false;
    motiveType = null;
    uuid = 'x' + (StringUtils.uuid() as string).replace(/\-/g, '');
    initialized = false;
    private effectiveMotives: MotiveConfig;

    motiveLocationDisplayedAfterUserInteration = true;
    userInteractedWithPage = false;

    // Preact fix
    // @ViewChild('parent') parent: ElementRef;
    // @ViewChild('container') container: ElementRef;
    parent = createRef<HTMLDivElement>();
    container = createRef<HTMLDivElement>();

    publisher = 'xconvert-com'
    placementName;
    slotId;
    targeting = {};

    /*** This is no longer used by the shouldShowAds method */
    get meetsUserInteractionCondition() {
        return !this.motiveLocationDisplayedAfterUserInteration || (this.motiveLocationDisplayedAfterUserInteration && this.userInteractedWithPage);
    }

    get shouldShowAds() {
        return this.state.showAds && (this.currentMotiveSlotIsEnabled || false) && (this.meetsUserInteractionCondition || true);
    }

    @lazyInject(MotivesService) private motivesService: MotivesService;
    @lazyInject(WindowService) private windowService: WindowService;
    @lazyInject(EnvironmentService) private environmentService: EnvironmentService;

    readonly state: Readonly<MotiveComponentState> = { showAds: this.props.showAds, filled: false, creativeVisible: false, styles: {} }
    static counter = 1;

    private insObserver: MutationObserver | null = null;
    private insResize: ResizeObserver | null = null;
    private insEl: HTMLModElement | null = null;

    /**
     * R14-Q8 (QA R14_QA_studio_1 row R14-Q8, UX R14-U28): the close key belongs to an AD, not to the
     * box. Bound to the `<ins>` for its life: AdSense writes `data-ad-status` on it when it answers
     * (`filled` / `unfilled`); an ad blocker leaves it unstamped. `filled` is read off the element
     * and kept in state, so the key renders exactly while a creative is on screen -- never over the
     * collapsed unit (three "×" on /downloads with ads blocked, one beside an unfilled unit with
     * ads live, one over the phone editor's empty sticky slot). A stable instance property, so
     * React calls it once on mount and once (null) on unmount, not on every render.
     *
     * R14-SH6 (QA R14_QA_studio_2 group 13): `filled` alone is not "on screen". The verdict is paired
     * with a measurement of the unit's box (`creativeIsVisible`): a MutationObserver over the whole unit
     * (the stamp, the creative iframe AdSense inserts and sizes), a ResizeObserver on the `<ins>` and its
     * container, and the window's `resize` (the rails' `d-none d-lg-block` column flips at 992 px).
     */
    private insRef = (el: HTMLModElement | null) => {
        this.unwatchIns();
        if (!el) {
            if (this.state.filled || this.state.creativeVisible) this.setState({ filled: false, creativeVisible: false });
            return;
        }
        this.insEl = el;
        if (typeof MutationObserver !== 'undefined') {
            this.insObserver = new MutationObserver(this.evaluateKey);
            this.insObserver.observe(el, { attributes: true, attributeFilter: [AD_STATUS, 'style', 'width', 'height'], childList: true, subtree: true });
        }
        if (typeof ResizeObserver !== 'undefined') {
            this.insResize = new ResizeObserver(this.evaluateKey);
            this.insResize.observe(el);
            if (el.parentElement) this.insResize.observe(el.parentElement);
        }
        if (typeof window !== 'undefined') window.addEventListener('resize', this.evaluateKey);
        this.evaluateKey();
    };

    private unwatchIns() {
        this.insObserver?.disconnect();
        this.insObserver = null;
        this.insResize?.disconnect();
        this.insResize = null;
        if (typeof window !== 'undefined') window.removeEventListener('resize', this.evaluateKey);
        this.insEl = null;
    }

    /** The key's two conditions, read off the live `<ins>`: AdSense's `filled` AND a rendered creative. */
    private evaluateKey = () => {
        const el = this.insEl;
        if (!el) return;
        const filled = el.getAttribute(AD_STATUS) === 'filled';
        const creativeVisible = filled && creativeIsVisible(el);
        if (filled !== !!this.state.filled || creativeVisible !== !!this.state.creativeVisible) {
            this.setState({ filled, creativeVisible });
        }
    };

    componentWillUnmount() {
        this.unwatchIns();
    }
    constructor(props: MotiveComponentProps) {
        super(props);
        this.slotId = 'in_content_ad_' + (MotiveComponent.counter++);

        if (this.props.location.match(/left/i)) {
            this.placementName = `xconvert_sidewall_left`
        } else if (this.props.location.match(/right/i)) {
            this.placementName = `xconvert_sidewall_right`
        } else if (this.props.location.match(/top/i) || this.props.location.match(/title/i)) {
            this.placementName = `xconvert_header`
        } else if (this.props.location.match(/bottom/i)) {
            this.placementName = `xconvert_footer`
        }
    }

    private selectValueBasedOnWindowWidth(sm, md, lg) {
        if (!BrowserUtils.hasWindow()) {
            return lg;
        }
        if (WindowUtils.getWindowSize() <= WindowSize.SM) {
            return sm
        }
        if (WindowUtils.getWindowSize() <= WindowSize.MD) {
            return md
        }
        return lg
    }

    componentDidMount() {
        this.windowService.userEngaged.subscribe({
            next: () => {
                this.userInteractedWithPage = true;
                this.updateView();
            }
        });

        this.motivesService.onEffectiveMotiveSettingsChange.pipe(untilDestroyed(this)).subscribe({
            next: (motives) => {
                this.effectiveMotives = motives;
                this.motiveLocationDisplayedAfterUserInteration = !(this.effectiveMotives.bypassMouseEvents || []).includes(this.mergedOptions.location);
                this.setUpMotiveRotation();
                this.updateView();
            },
            error: (error) => this.logger.error(error),
        });

        // if (this.container && this.container.nativeElement) {
        //     /* tslint:disable */
        //     const flags = RendererStyleFlags2.DashCase | RendererStyleFlags2.Important;
        //     // this.renderer.setStyle(this.container.nativeElement, 'margin', 'auto');
        //     // this.renderer.setStyle(this.parent.nativeElement, 'width', this.containerWidth, flags);
        //     // this.renderer.setStyle(this.parent.nativeElement, 'height', this.containerHeight, flags);
        // }

        if (BrowserUtils.hasWindow()) {
            if (this.props.location.match(/left/i)) {
                this.setState({
                    styles: { display: this.selectValueBasedOnWindowWidth('none', 'block', 'block') }
                })

            } else if (this.props.location.match(/right/i)) {
                this.setState({
                    styles: { display: this.selectValueBasedOnWindowWidth('none', 'block', 'block') }
                })
            } else if (this.props.location.match(/top/i) || this.props.location.match(/title/i) || this.props.location.match(/bottom/i)) {
                this.setState({
                    styles: {
                        display: this.selectValueBasedOnWindowWidth('block', 'block', 'block'),
                        height: this.selectValueBasedOnWindowWidth('120px', '120px', '120px'),
                        maxHeight: this.selectValueBasedOnWindowWidth('120px', '120px', '120px')
                    }
                })
            }
        }
        this.initialized = true;
    }

    /**
     * Automatic ad refresh is deliberately not implemented.
     *
     * The settings document still carries `rotateMotives` (prod: true, every 125s +/- 35s) from
     * when this component last rendered anything. Honouring it now would refresh the ad several
     * times per visit, because a converter page holds the user while their job runs. Refreshed
     * impressions are low-viewability -- they drag RPM down on the slots that remain -- and a
     * short forced cycle is the pattern that gets an AdSense account looked at. The account is
     * the revenue channel, so it is worth more than the impressions a timer would add.
     *
     * The setting is left in the schema rather than removed so nothing else reading the
     * settings document breaks. See docs/proposed/AD_PLACEMENT_DESIGN.md 4.3.
     */
    setUpMotiveRotation() {
        // intentionally empty
    }

    @reloadAfterExecute()
    updateView() {
        if (this.effectiveMotives) {
            this.motiveType = this.effectiveMotives && this.effectiveMotives.externalMotiveType;
        }

        if (this.mergedOptions.showAds) {
            // Round 13 (Z4): the ONE predicate the editors use too (`ads-would-show.ts`); `null` = pending.
            this.currentMotiveSlotIsEnabled = adsWouldShowFor(this.effectiveMotives, this.mergedOptions.location) === true;
            if (this.currentMotiveSlotIsEnabled) {
                this.runMotives();
                this.updateWidth();
            }
        }
    }


    shouldComponentUpdate(nextProp: MotiveComponentProps, nextState: MotiveComponentState) {
        // Was `nextProp.data !== nextProp.data`, which is always false, so no parent re-render
        // ever reached this component -- and because React skips componentDidUpdate when
        // shouldComponentUpdate returns false, the showAds prop handler below was dead too.
        // Harmless while render() returned an empty span; not harmless now.
        // R14-Q8: the other state changes reach the DOM through `updateView()`'s forceUpdate; the
        // fill verdict arrives from a MutationObserver with no such call, so it is compared here.
        return nextProp.data !== this.props.data || nextProp.showAds !== this.props.showAds
            || !!nextState.filled !== !!this.state.filled
            || !!nextState.creativeVisible !== !!this.state.creativeVisible;
    }

    componentDidUpdate(prevProps: MotiveComponentProps) {
        if (prevProps.showAds != this.props.showAds) {
            this.setState({
                showAds: this.props.showAds,
            });
            this.updateView();
        }
    }

    private updateWidth() {
        const widthAsNumber = parseInt(this.mergedOptions.adWidth);
        if (isNumber(widthAsNumber) && !isNaN(widthAsNumber)) {
            try {
                if (window && (window.innerWidth < widthAsNumber)) {
                    this.mergedOptions.adWidth = toString(window.innerWidth) + 'px';
                }
            } catch {
                // prevent throw in SSR mode
            }
        }
    }

    runMotives() {
        if (this.motiveType === 'buysell') {
            //  <script *ngIf="motiveType == 'buysell'" async type="text/javascript" src="//cdn.carbonads.com/carbon.js?serve=CE7DC27L&placement=wwwxconvertcom" id="_carbonads_js"></script>

            const node = document.createElement('script');
            node.src = "//cdn.carbonads.com/carbon.js?serve=CE7DC27L&placement=wwwxconvertcom";
            node.type = 'text/javascript';
            node.async = true;
            node.id = this.uuid + "_carbonads_js",
                node.onload = () => {

                };
            setTimeout(() => {
                document.getElementById(this.uuid).appendChild(node);
            }, 100);
            return;
        }
        setTimeout(() => {
            try {
                const viewWidth = window && window.innerWidth;
                if (this.shouldFitToContainer()) {
                    // TODO Preact see that angular does
                    // const rect = this.containerElemRef.current.getBoundingClientRect();
                    // this.renderer.setStyle(this.containerElemRef.nativeElement, 'height', '90px');
                    // this.renderer.setStyle(this.containerElemRef.nativeElement, 'width', rect.width + 'px');
                    // this.renderer.setStyle(this.parent.nativeElement, 'width', rect.width + 'px');
                }
                // Only request an ad when this slot exists and hasn't been filled yet.
                // Re-pushing an already-filled <ins> makes AdSense throw a benign
                // "already have ads" TagError; runMotives() re-runs often (engagement,
                // settings changes, rotation, prop updates), so without this guard that
                // error floods our logs hundreds of thousands of times a day.
                // ONE push in flight per page (`requestAdFill`): a second push queued while the
                // first is pending is exactly the TagError S-50 measured on every Studio load.
                requestAdFill(this.container.current);
            } catch (e) {
                // A residual benign TagError can still slip through under races. It is
                // expected and non-actionable, so log at debug (not forwarded to the
                // backend in prod) rather than error to keep it out of our logs.
                this.logger.debug('adsbygoogle push skipped: ' + (e && e.message));
            }
        }, 400);
    }


    shouldFitToContainer() {
        return this.mergedOptions.containerWidth === '100%';
    }

    toggleGContent() {
        this.setState({
            showAds: !this.state.showAds,
            dismissedByUser: this.state.showAds,
        });
        this.updateView();
    }

    showGContent() {
        this.setState({
            showAds: true,
        });
        this.updateView();
    }

    getMotiveOrPlaceholder() {
        if (this.shouldShowAds) {
            return <div className={`m-container ${styles['external-motives']}`} ref={this.parent}>
                <div ref={this.container} className={`gcontainer ${styles['gcontainer']} ${styles[this.props.className]}`}>
                    {
                        this.motiveType == 'google' && <ins ref={this.insRef} className={`adsbygoogle ${styles[this.props.className]}`}
                            style={{ display: 'inline-block', textAlign: 'center' }} data-ad-client={AD_CLIENT}
                            data-ad-slot={this.mergedOptions.data}></ins>
                    }
                    {/* R14-Q8: the close key only while AdSense says a creative is on screen (`insRef`).
                        R14-SH6: ... AND the creative has a rendered box; and it lives INSIDE the unit's box.
                        `.m-container` is as wide as its column while `.gcontainer` is the IAB size centred in it
                        (`margin: 0 auto`), so a key anchored to `.m-container` floated at the column's corner --
                        287 px right of the 970 x 90 on /downloads at 1440, an "×" over blank space (group 13).
                        R14-SH8: the creative's TOP-LEFT corner, not its top-right -- that corner is where AdSense
                        draws its own AdChoices "i"/triangle (and its overlay ads' own "Close ad"); SH6's key sat on
                        it (846,541 on the 300 x 250). The look is the module's `.close` (kit tokens, 24 px fine /
                        44 px coarse, a semi-opaque disc so the glyph reads on a light and a dark creative); the
                        inline style went with it. `gclose` stays for the probes and the T4.5 spec. */}
                    {this.state.filled && this.state.creativeVisible && <button type="button" className={`gclose ${styles['close']}`}
                    aria-label="Close ad" title="Close ad"
                    onClick={() => this.toggleGContent()}>&times;</button>}
                </div>
            </div>
        }
        // Round 13 (Z4, owner review 5 S4/P2) rendered nothing once the answer was "no ad here" for ANY reason; the
        // CLS ruling below narrows that to the served document's "no".
        // CLS lane (review 7, owner ruling 2026-09-27): the box goes only when the SERVED document has no ad here
        // (`adSpaceFor`: paid, position off). The ad library found blocked or an ad closed within the hour keeps the blank
        // reserved box -- it used to go ~1.4 s in (0.083 CLS on the phone converter with the ad hosts blocked).
        if (!this.state.dismissedByUser && adSpaceFor(this.effectiveMotives, this.mergedOptions.location) === false) {
            return null;
        }
        return <div className={`m-container ${styles['m-container']} ${styles['external-motives']}`} data-ad-pending={this.effectiveMotives ? undefined : 'true'}>
            <div className={`no-m gcontainer ${styles['gcontainer']} ${styles[this.props.className]}`}>
                {
                    /*
                     * Closing an ad is the one moment we know for certain the user does not want
                     * ads, so it is where the ad-free offer belongs -- not only on the pricing
                     * page they have no reason to visit. It renders inside the same reserved
                     * box the ad occupied, so nothing on the page moves.
                     *
                     * Only after a real dismissal: a slot that was never enabled (every paid
                     * user, every disabled location) keeps the silent placeholder, because
                     * selling ad-free to someone who already has it is noise.
                     *
                     * docs/proposed/RETENTION_FIXES.md T4.5
                     */
                    this.state.dismissedByUser
                        ? <div className={styles['upgrade-offer']}>
                            Ads keep XConvert free.{' '}
                            {/* /pricing does not exist (404). The copy names one specific
                                plan, so link straight to it -- owner call 2026-09-09. */}
                            <a href="/product/backer_monthly">Go ad-free from $4.99/month</a>
                        </div>
                        : <div></div>
                }
            </div>
        </div>
    }

    // getFreestarMotiveOrPlaceholder() {
    //     if (!this.placementName || !this.shouldShowAds) {
    //         return <div  className={`${(this.placementName && styles[this.placementName]) || 'empty hidden'}`}>
    //         </div>
    //     }
    //     return <>
    //         <div className={`${(this.placementName && styles[this.placementName]) || ''}`} style={{
    //             ...this.state.styles,
    //             alignItems: 'flex-start',
    //             display: 'flex',
    //             justifyContent: 'flex-start'
    //         }}>
    //             {
    //             <FreestarAdSlot
    //                 publisher={this.publisher}
    //                 placementName={this.placementName}
    //                 slotId={`${this.props.location}`}
    //                 targeting={this.targeting}
    //                 channel='custom_channel'
    //                 classList={[]}
    //             />
    //             }
    //         </div>
    //     </>
    // }

    render() {
        this.mergedOptions = Object.assign({}, defaultOptions, this.props);
        // return this.getFreestarMotiveOrPlaceholder();
        return this.getMotiveOrPlaceholder();
    }
}
